"use client";
import { evaluate, toast } from "@kyan/ui";

import { defaultUnderlying } from "#/config/markets";
import { optionRfqSizes } from "#/config/orders";
import { usePortfolioValidations } from "#/hooks/usePortfolioValidations";
import { usePostRFQRequest } from "#/services/kyan/hooks/usePostRFQRequest";
import { useIsDevMode } from "#/store/app/devMode/devMode";
import { useMarket } from "#/store/app/portfolio/market";
import { usePositionsDrawerMarket } from "#/store/app/positionsDrawer/positionsDrawerMarket";
import { usePositionsDrawerRfqTab } from "#/store/app/positionsDrawer/positionsDrawerRfqTab";
import { usePositionsDrawerSettings } from "#/store/app/positionsDrawer/positionsDrawerSettings";
import { usePositionsDrawerTab } from "#/store/app/positionsDrawer/positionsDrawerTab";
import { useComboInstruments } from "#/store/combo/comboInstruments";
import { useAccount } from "#/store/wallet/account/account";
import { createOptionInstrumentName } from "#/utils/instrument.utils";

export const useComboRfqController = () => {
  const { setPositionsDrawerTab } = usePositionsDrawerTab();
  const { setPositionsDrawerSettings } = usePositionsDrawerSettings();
  const { setPositionsDrawerRfqTab } = usePositionsDrawerRfqTab();
  const { setMarket: setPositionsDrawerMarket } = usePositionsDrawerMarket();
  const { market } = useMarket();
  const { isLiquidated, hasZeroEquity } = usePortfolioValidations();
  const { account } = useAccount();
  const { comboInstruments } = useComboInstruments();
  const { isDevMode } = useIsDevMode();

  const areAllOptionsInstruments = comboInstruments.every(
    (instrument) => instrument.type === "options",
  );
  const meetMinimumSizeRequirements = comboInstruments.every((instrument) => {
    const instrumentMarket = instrument?.underlying ?? defaultUnderlying;
    const [minSize] = optionRfqSizes[instrumentMarket];
    return Number(instrument.size) >= minSize;
  });

  const isDisabled =
    (hasZeroEquity ||
      !areAllOptionsInstruments ||
      !meetMinimumSizeRequirements ||
      isLiquidated) &&
    !isDevMode;

  const label = evaluate({
    "Liquidation Active": isLiquidated,
    "Options only": !areAllOptionsInstruments,
    "Input size": !meetMinimumSizeRequirements,
    "No equity": hasZeroEquity,
    "Request RFQ": true,
  });
  const buttonLabel = `${label}${isDevMode ? " (Dev)" : ""}`;

  const { mutate, isPending } = usePostRFQRequest({
    onSuccess: (response) => {
      toast.success({
        title: "RFQ request successful",
        description: response,
      });

      setPositionsDrawerTab("rfq");
      setPositionsDrawerMarket(market);
      setPositionsDrawerRfqTab("options");
      setPositionsDrawerSettings({ show: true });
    },
    onError: (error) => {
      toast.error({
        title: "Error with RFQ request",
        description: error.message,
      });
    },
  });

  const handleTrade = () => {
    const rfqRequest = {
      taker: account ?? "",
      rfq_orders: comboInstruments.map(
        ({ optionType, underlying, strike, maturity, side, size }) => ({
          instrument_name: createOptionInstrumentName({
            isCall: optionType === "call",
            optionType,
            underlying,
            strike,
            maturity,
          }),
          direction: side,
          contracts: Number(size),
        }),
      ),
    };

    mutate(rfqRequest);
  };

  return {
    buttonLabel,
    isLoading: isPending,
    isDisabled,
    handleTrade,
  };
};
